/*
 * EventTabs — primary navigation strip used on every event sub-page.
 *
 * Pill-style tab nav that lives between the hero and page body. Active tab
 * is derived from the current URL so the same Link instances work as
 * forward navigation AND as the active-state source of truth.
 *
 * Also renders a tiny event identity badge (logo + name) at the left of
 * the strip so the user always knows which event they're inside. The
 * badge pulls from Zustand so we don't need to thread props through
 * every page — falls back gracefully if the store hasn't loaded yet.
 *
 * To add a tab, add an entry to TABS. Path is relative to /tournaments/:eventId.
 */

import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { useTournamentStore } from '@/store/tournamentStore';
import { EventLogo } from '@/components/EventLogo';

interface Tab {
  key: string;
  label: string;
  /** Path suffix appended to /app/tournaments/:eventId. */
  path: string;
}

const TABS: Tab[] = [
  // Live Scoring was retired: the Teams tab is the live view now (it
  // absorbed TV Mode, the clinch progress bar, and player records).
  // /leaderboard redirects to /teams for old links; /leaderboard/tv
  // (TV Mode) still exists, linked from the Teams tab.
  { key: 'teams',       label: 'Teams',       path: '/teams' },
  { key: 'players',     label: 'Players',     path: '/players' },
  { key: 'rounds',      label: 'Rounds',      path: '/rounds' },
  { key: 'mymatch',     label: 'My Match',    path: '/my-matches' },
  { key: 'settings',    label: 'Settings',    path: '/edit' },
];

// Per the roles spec (Q3): players see Teams, Rounds, Live Scoring only.
// Captains and owners see everything. Settings stays gated to owners
// since Edit Tournament covers event-wide config the captain shouldn't
// need (and the underlying delete/archive are owner-only by Q2).
const OWNER_ONLY_TABS = new Set(['settings']);
const HIDE_FROM_PLAYER = new Set(['players', 'settings']);

export function EventTabs({ eventId }: { eventId: string }) {
  const loc = useLocation();
  const activeEvent = useTournamentStore((s) => s.activeEvent);
  const base = `/app/tournaments/${eventId}`;

  // Show the event identity only when the store has the matching event
  // loaded. On a fresh navigation the page that hosts EventTabs always
  // fetches into Zustand within a render or two, so the badge appears
  // almost immediately.
  const event = activeEvent?.id === eventId ? activeEvent : null;
  const isOwner = event?.myRole === 'owner';
  const isCaptainOrOwner = isOwner || event?.myRole === 'captain';

  // Filter tabs by role. Settings is owner-only (delete/archive guardrail).
  // Players tab is hidden from the 'player' role per the spec.
  const visibleTabs = TABS.filter((t) => {
    if (OWNER_ONLY_TABS.has(t.key) && !isOwner) return false;
    if (HIDE_FROM_PLAYER.has(t.key) && !isCaptainOrOwner) return false;
    return true;
  });

  // Match by deepest-matching path so /leaderboard/tv still highlights
  // Leaderboard, /rounds/:roundId highlights Rounds, etc.
  const active =
    visibleTabs.slice().sort((a, b) => b.path.length - a.path.length)
        .find((t) => loc.pathname.startsWith(`${base}${t.path}`))
    ?? null;

  // Keep the active tab in view across navigations. EventTabs re-renders
  // on every route change in AppShell, which resets the horizontal scroll
  // position back to 0. On mobile that means tapping "Live Scoring"
  // scrolls the strip back to the left and you can't see the tab you
  // just selected. Auto-scrolling the active tab into view fixes it
  // without changing the layout when everything already fits on screen.
  const stripRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLAnchorElement | null>(null);
  useEffect(() => {
    const strip = stripRef.current;
    const el = activeRef.current;
    if (!strip || !el) return;
    // 'nearest' on the inline axis = scroll the minimum needed to make
    // the active tab fully visible, then stop. Doesn't push or pull
    // when the tab is already on screen. Behavior 'auto' to skip the
    // animation on first paint (instant on mount, smooth on subsequent
    // taps is also fine — we choose 'auto' since browsers vary).
    el.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [active?.key]);

  return (
    <div className="event-tabs">
      {/* Identity: inline with the tabs on desktop; its own CENTERED row
          on phones (Travis's spec) so the event name reads as the page
          title instead of competing with the tab strip. */}
      {event && (
        <Link to={base} title={event.name} className="event-tabs-identity">
          <EventLogo logoUrl={event.logoUrl} name={event.name} size={24} />
          <span className="event-tabs-identity-name">{event.name}</span>
        </Link>
      )}
      <div className="event-tabs-scroller">
        <div ref={stripRef} className="event-tabs-strip no-scrollbar">
          {visibleTabs.map((t) => {
            const isActive = active?.key === t.key;
            return (
              <Link
                key={t.key}
                to={`${base}${t.path}`}
                ref={isActive ? activeRef : undefined}
                style={{
                  // Microsoft-Teams-style tabs: text-only, underline
                  // indicates the active tab. 12px vertical padding keeps
                  // each tap target at ~42-44px on phones.
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '12px 6px',
                  color: isActive ? 'var(--text)' : 'var(--text-sec)',
                  fontSize: 14,
                  fontWeight: isActive ? 800 : 600,
                  letterSpacing: 0.1,
                  textDecoration: 'none',
                  flexShrink: 0,
                  transition: 'color 80ms ease',
                }}
              >
                {t.label}
                {isActive && (
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 4,
                      right: 4,
                      bottom: -1,
                      height: 2,
                      background: 'var(--green-bright)',
                      borderRadius: 2,
                    }}
                  />
                )}
              </Link>
            );
          })}
        </div>
        {/* Right-edge fade + chevron: signals there are more tabs to the
            right. CSS hides it on desktop and when the strip fits. */}
        <span className="event-tabs-more" aria-hidden>
          <ChevronRight width={14} height={14} />
        </span>
      </div>
    </div>
  );
}
